import type {
    DOMConversionMap,
    DOMConversionOutput,
    DOMExportOutput,
    EditorConfig,
    ElementFormatType,
    LexicalEditor,
    LexicalNode,
    NodeKey,
    Spread,
} from 'lexical';

import { BlockWithAlignableContents } from '@lexical/react/LexicalBlockWithAlignableContents';
import { DecoratorBlockNode, SerializedDecoratorBlockNode } from '@lexical/react/LexicalDecoratorBlockNode';
import {convertUrlToEmbed} from "../../../../shared/utility";

type VideoComponentProps = Readonly<{
    className: Readonly<{
        base: string;
        focus: string;
    }>;
    format: ElementFormatType | null;
    nodeKey: NodeKey;
    videoURL: string;
}>;

function VideoComponent({ className, format, nodeKey, videoURL }: VideoComponentProps) {
    return (
        <BlockWithAlignableContents className={className} format={format} nodeKey={nodeKey}>
            <iframe
                width="320"
                height="220"
                src={videoURL}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen={true}
                title="Video"
            />
        </BlockWithAlignableContents>
    );
}

export type SerializedVideoNode = Spread<
    {
        videoURL: string;
        type: 'video';
        version: 1;
    },
    SerializedDecoratorBlockNode
>;

function convertVideoElement(domNode: HTMLElement): null | DOMConversionOutput {
    let videoURL = domNode.getAttribute('data-lexical-video');

    // If data-lexical-video is missing, try to get the URL from the src attribute
    if (!videoURL) {
        videoURL = domNode.getAttribute('src');
    }

    if (videoURL) {
        const node = $createVideoNode(videoURL);
        return { node };
    }

    return null;
}

export class VideoNode extends DecoratorBlockNode {
    __id: string;

    static getType(): string {
        return 'video';
    }

    constructor(id: string, format?: ElementFormatType, key?: NodeKey) {
        const embedUrl = convertUrlToEmbed(id);

        super(format, key);
        this.__id = embedUrl;
    }

    static clone(node: VideoNode): VideoNode {
        return new VideoNode(node.__id, node.__format, node.__key);
    }

    static importJSON(serializedNode: SerializedVideoNode): VideoNode {
        const embedUrl = convertUrlToEmbed(serializedNode.videoURL);
        const node = $createVideoNode(embedUrl);

        node.setFormat(serializedNode.format);

        return node;
    }

    exportJSON(): SerializedVideoNode {
        return {
            ...super.exportJSON(),
            type: 'video',
            version: 1,
            videoURL: this.__id,
        };
    }

    exportDOM(): DOMExportOutput {
        const element = document.createElement('iframe');

        element.setAttribute('data-lexical-video', this.__id);
        element.setAttribute('width', '320');
        element.setAttribute('height', '220');
        element.setAttribute('src', this.__id);
        element.setAttribute('frameborder', '0');
        element.setAttribute(
            'allow',
            'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
        );
        element.setAttribute('allowfullscreen', 'true');
        element.setAttribute('title', 'Video video');

        return { element };
    }

    static importDOM(): DOMConversionMap | null {
        return {
            iframe: (domNode: HTMLElement) => {
                // Check if either data-lexical-video or src is present
                if (!domNode.hasAttribute('data-lexical-video') && !domNode.hasAttribute('src')) {
                    return null;
                }

                return {
                    conversion: convertVideoElement,
                    priority: 1,
                };
            },
        };
    }

    updateDOM(): false {
        return false;
    }

    getId(): string {
        return this.__id;
    }

    getTextContent(_includeInert?: boolean | undefined, _includeDirectionless?: false | undefined): string {
        return this.__id;
    }

    decorate(_editor: LexicalEditor, config: EditorConfig): JSX.Element {
        const embedBlockTheme = config.theme.embedBlock || {};
        const className = {
            base: embedBlockTheme.base || '',
            focus: embedBlockTheme.focus || '',
        };

        return <VideoComponent className={className} format={this.__format} nodeKey={this.getKey()} videoURL={this.__id} />;
    }

    isInline(): false {
        return false;
    }
}

export function $createVideoNode(videoURL: string): VideoNode {
    const embedUrl = convertUrlToEmbed(videoURL);

    return new VideoNode(embedUrl);
}

export function $isVideoNode(node: VideoNode | LexicalNode | null | undefined): node is VideoNode {
    return node instanceof VideoNode;
}
